import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { useDealCompensation } from 'hooks/usePartnerAdmin'
import {
  markCompensationReceived,
  setCompensation,
} from 'submission/lib/partner-api'
import { bipsToPercent, formatDate, percentToBips } from 'views/Partners/format'
import { useResyncingDraft } from 'views/Partners/useResyncingDraft'
import type { CompensationForm, DealCompensation } from 'views/Partners/types'

type Props = { dealId: string }

const FORMS: CompensationForm[] = ['cash', 'token', 'equity']

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-neutral-100'

type Draft = {
  form: CompensationForm
  ratePercent: string
}

const toDraft = (c: DealCompensation | null): Draft =>
  c
    ? { form: c.form, ratePercent: bipsToPercent(c.rate_bips) }
    : { form: 'cash', ratePercent: '0.00' }

const CompensationPanel: React.FC<Props> = ({ dealId }) => {
  const { showToast } = useToast()
  const { data, error, isLoading, mutate } = useDealCompensation(dealId)
  const [draft, setDraft, dirty] = useResyncingDraft(data, toDraft)
  const [saving, setSaving] = useState(false)
  const [markingReceived, setMarkingReceived] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const received = Boolean(data?.received_at)
  // Save and Mark-received both act on the same compensation record, and
  // the spec treats it as lockable (rate cannot change once received): they
  // must not be allowed to run concurrently. A single `busy` flag disables
  // both actions while either is in flight, even though each has its own
  // state (`saving` / `markingReceived`) for correctness of its own
  // pending-vs-idle transition.
  const busy = saving || markingReceived

  const save = async () => {
    if (!draft) return
    const bips = draft.form === 'cash' ? percentToBips(draft.ratePercent) : 0
    if (bips === null || bips < 0 || bips > 10000) {
      setLocalError('Rate must be between 0% and 100%')
      return
    }
    setLocalError(null)
    setSaving(true)
    try {
      const saved = await setCompensation(dealId, {
        form: draft.form,
        rate_bips: bips,
      })
      showToast({ type: 'success', message: 'Compensation saved' })
      // Sync the draft to the saved value immediately, rather than waiting
      // for the SWR revalidation to round-trip: the user's raw input
      // (e.g. "2.5") and the canonical re-derived value (e.g. "2.50") are
      // both valid but string-unequal, which would otherwise leave `dirty`
      // true — and Mark received disabled — until an unrelated revalidation
      // happened to reset it.
      setDraft(toDraft(saved))
      mutate(saved, { revalidate: false })
    } catch (e) {
      showToast({ type: 'error', message: (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  const markReceived = async () => {
    if (!data) return
    if (
      !window.confirm(
        `Mark compensation as received at ${bipsToPercent(
          data.rate_bips,
        )}%? Pending deal shares become claimable and the rate is locked.`,
      )
    )
      return
    setMarkingReceived(true)
    try {
      await markCompensationReceived(dealId)
      showToast({ type: 'success', message: 'Compensation marked received' })
      mutate()
    } catch (e) {
      showToast({ type: 'error', message: (e as Error).message })
    } finally {
      setMarkingReceived(false)
    }
  }

  return (
    <section className="border border-neutral-200 rounded-lg p-4 space-y-3">
      <h2 className="font-semibold">Compensation</h2>
      {error && (
        <div className="text-sm text-red-600">
          Failed to load: {(error as Error).message}
        </div>
      )}
      {isLoading && <div className="text-sm text-neutral-600">Loading…</div>}
      {!isLoading && !error && draft && (
        <>
          <div className="text-xs text-neutral-500">
            {data
              ? received
                ? `Received ${formatDate(
                    data.received_at,
                  )} · rate locked · updated by ${data.updated_by}`
                : `Set by ${data.updated_by} on ${formatDate(
                    data.updated_at,
                  )} · not yet received`
              : 'No compensation recorded for this deal. Deal shares accrue only for cash compensation with a rate above 0%.'}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-sm">
              Form
              <select
                className={inputCls}
                value={draft.form}
                onChange={(e) =>
                  setDraft(
                    (d) =>
                      d && { ...d, form: e.target.value as CompensationForm },
                  )
                }
                disabled={received}
              >
                {FORMS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Rate % (cash only)
              <input
                className={inputCls}
                value={draft.ratePercent}
                onChange={(e) =>
                  setDraft((d) => d && { ...d, ratePercent: e.target.value })
                }
                disabled={received || draft.form !== 'cash'}
              />
            </label>
          </div>
          {localError && (
            <div className="text-sm text-red-600">{localError}</div>
          )}
          <div className="flex flex-col items-end gap-1">
            <div className="flex justify-end gap-2">
              {data && !received && data.form === 'cash' && (
                <Button
                  variant="outline"
                  onClick={markReceived}
                  disabled={busy || dirty}
                >
                  Mark received
                </Button>
              )}
              <Button onClick={save} disabled={busy || received}>
                Save compensation
              </Button>
            </div>
            {data && !received && data.form === 'cash' && dirty && (
              <span className="text-xs text-neutral-500">
                Save changes first
              </span>
            )}
          </div>
        </>
      )}
    </section>
  )
}

export default CompensationPanel
